import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, endpoints, useTheme } from "../lib/api";
import { LayoutDashboard, FolderGit2, Cpu, Briefcase, GraduationCap, Award, Trophy, MessageSquare, FileText, Settings, Mail, LogOut, Plus, Trash2, Save, Home, Sun, Moon, Search } from "lucide-react";

const TYPES = [
    { key: "projects", label: "Projects", icon: FolderGit2 },
    { key: "skills", label: "Skills", icon: Cpu },
    { key: "experience", label: "Experience", icon: Briefcase },
    { key: "education", label: "Education", icon: GraduationCap },
    { key: "certifications", label: "Certifications", icon: Award },
    { key: "achievements", label: "Achievements", icon: Trophy },
    { key: "testimonials", label: "Testimonials", icon: MessageSquare },
    { key: "blogs", label: "Blogs", icon: FileText },
];

function Login({ onAuth }) {
    const [pw, setPw] = useState("");
    const [loading, setLoading] = useState(false);
    const submit = async (e) => {
        e.preventDefault(); setLoading(true);
        try { const { token } = await endpoints.login(pw); localStorage.setItem("admin_token", token); onAuth(token); toast.success("Welcome back"); }
        catch { toast.error("Invalid password"); } finally { setLoading(false); }
    };
    return (
        <div className="min-h-screen grid place-items-center bg-background px-5">
            <form onSubmit={submit} className="glass rounded-3xl p-8 w-full max-w-sm gradient-border" data-testid="admin-login">
                <div className="font-display text-2xl font-semibold mb-1">Admin Access</div>
                <p className="text-sm text-muted-foreground mb-6">Portfolio content management</p>
                <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="admin-password" placeholder="Password"
                    className="w-full bg-secondary border border-border rounded-xl px-4 py-3 text-sm outline-none focus:border-primary mb-4" />
                <button disabled={loading} data-testid="admin-login-submit" className="w-full bg-foreground text-background rounded-xl py-3 text-sm font-medium hover:opacity-90 disabled:opacity-60">{loading ? "Signing in…" : "Sign In"}</button>
                <a href="/" className="block text-center text-xs text-muted-foreground mt-4 hover:text-primary">← Back to site</a>
            </form>
        </div>
    );
}

function Editor({ token, type }) {
    const api = adminApi(token);
    const [items, setItems] = useState([]);
    const [drafts, setDrafts] = useState({});
    const load = () => endpoints.content(type).then((d) => { setItems(d); const dd = {}; d.forEach((x) => (dd[x.id] = JSON.stringify(stripMeta(x), null, 2))); setDrafts(dd); });
    useEffect(() => { load(); }, []);

    const stripMeta = (x) => { const { id, created_at, order, ...rest } = x; return rest; };

    const save = async (id) => {
        try { const data = JSON.parse(drafts[id]); await api.updateContent(type, id, data); toast.success("Saved"); load(); }
        catch (e) { toast.error("Invalid JSON or save failed"); }
    };
    const del = async (id) => { if (!window.confirm("Delete this item?")) return; await api.deleteContent(type, id); toast.success("Deleted"); load(); };
    const add = async () => { try { await api.createContent(type, { title: "New item", summary: "" }); toast.success("Created"); load(); } catch { toast.error("Failed"); } };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-2xl font-semibold capitalize">{type}</h2>
                <button onClick={add} data-testid="admin-add-item" className="inline-flex items-center gap-2 bg-foreground text-background rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90"><Plus className="w-4 h-4" /> Add</button>
            </div>
            <div className="space-y-4">
                {items.map((it) => (
                    <div key={it.id} className="glass rounded-2xl p-4" data-testid={`admin-item-${it.id}`}>
                        <div className="flex items-center justify-between mb-3">
                            <div className="font-medium text-sm">{it.title || it.group || it.role || it.name || "Item"}</div>
                            <div className="flex gap-2">
                                <button onClick={() => save(it.id)} data-testid="admin-save-item" className="inline-flex items-center gap-1.5 text-xs bg-primary text-white rounded-lg px-3 py-1.5 hover:opacity-90"><Save className="w-3.5 h-3.5" /> Save</button>
                                <button onClick={() => del(it.id)} data-testid="admin-delete-item" className="inline-flex items-center gap-1.5 text-xs glass rounded-lg px-3 py-1.5 text-red-400 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                        </div>
                        <textarea value={drafts[it.id] || ""} onChange={(e) => setDrafts({ ...drafts, [it.id]: e.target.value })}
                            rows={8} className="w-full font-mono text-xs bg-secondary border border-border rounded-xl p-3 outline-none focus:border-primary" />
                    </div>
                ))}
                {items.length === 0 && <div className="text-sm text-muted-foreground py-10 text-center">No items yet. Click Add.</div>}
            </div>
        </div>
    );
}

function ConfigEditor({ token }) {
    const api = adminApi(token);
    const [json, setJson] = useState("");
    useEffect(() => { endpoints.config().then((c) => { const { key, _id, ...rest } = c; setJson(JSON.stringify(rest, null, 2)); }); }, []);
    const save = async () => { try { await api.updateConfig(JSON.parse(json)); toast.success("Config saved"); } catch { toast.error("Invalid JSON"); } };
    return (
        <div>
            <div className="flex items-center justify-between mb-6"><h2 className="font-display text-2xl font-semibold">Site Config</h2>
                <button onClick={save} data-testid="admin-save-config" className="inline-flex items-center gap-2 bg-primary text-white rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90"><Save className="w-4 h-4" /> Save</button></div>
            <p className="text-sm text-muted-foreground mb-4">Edit name, email, WhatsApp, resume URL, socials, availability, tagline, bio and more.</p>
            <textarea value={json} onChange={(e) => setJson(e.target.value)} rows={22} className="w-full font-mono text-xs bg-secondary border border-border rounded-xl p-4 outline-none focus:border-primary" data-testid="admin-config-editor" />
        </div>
    );
}

function Contacts({ token }) {
    const api = adminApi(token);
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const load = () => api.contacts().then(setItems).catch(() => { });
    useEffect(() => { load(); }, []);
    const del = async (id) => { await api.deleteContact(id); toast.success("Deleted"); load(); };
    const filtered = items.filter((i) => `${i.name} ${i.email} ${i.message} ${i.subject}`.toLowerCase().includes(q.toLowerCase()));
    return (
        <div>
            <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                <h2 className="font-display text-2xl font-semibold">Messages <span className="text-sm text-muted-foreground font-sans">({items.length})</span></h2>
                <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="admin-contacts-search" className="glass rounded-lg pl-9 pr-4 py-2 text-sm outline-none w-56" /></div>
            </div>
            <div className="space-y-3">
                {filtered.map((c) => (
                    <div key={c.id} className="glass rounded-2xl p-4" data-testid={`admin-contact-${c.id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="font-medium text-sm">{c.name} <span className="text-muted-foreground font-normal">· {c.email}</span></div>
                                <div className="text-xs text-primary font-mono mt-0.5">{c.subject}</div>
                                <p className="text-sm text-foreground/80 mt-2">{c.message}</p>
                                <div className="text-[10px] text-muted-foreground font-mono mt-2">{new Date(c.created_at).toLocaleString()}</div>
                            </div>
                            <button onClick={() => del(c.id)} data-testid="admin-delete-contact" className="text-red-400 hover:bg-red-500/10 rounded-lg p-2"><Trash2 className="w-4 h-4" /></button>
                        </div>
                    </div>
                ))}
                {filtered.length === 0 && <div className="text-sm text-muted-foreground py-10 text-center">No messages.</div>}
            </div>
        </div>
    );
}

export default function Admin() {
    const [token, setToken] = useState(() => localStorage.getItem("admin_token"));
    const [tab, setTab] = useState("projects");
    const { theme, toggle } = useTheme();
    if (!token) return <Login onAuth={setToken} />;
    const logout = () => { localStorage.removeItem("admin_token"); setToken(null); };

    const nav = [...TYPES, { key: "config", label: "Config", icon: Settings }, { key: "contacts", label: "Messages", icon: Mail }];

    return (
        <div className="min-h-screen flex bg-background">
            <aside className="w-60 shrink-0 border-r border-border p-4 hidden md:flex flex-col sticky top-0 h-screen">
                <div className="font-display font-bold text-lg mb-6 flex items-center gap-2"><span className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary to-accent grid place-items-center text-white text-sm">H</span> Studio</div>
                <nav className="flex-1 space-y-1 overflow-y-auto">
                    {nav.map((n) => (
                        <button key={n.key} onClick={() => setTab(n.key)} data-testid={`admin-nav-${n.key}`}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${tab === n.key ? "bg-secondary text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                            <n.icon className="w-4 h-4" /> {n.label}
                        </button>
                    ))}
                </nav>
                <div className="space-y-1 pt-3 border-t border-border">
                    <button onClick={toggle} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground">{theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />} Theme</button>
                    <a href="/" className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground"><Home className="w-4 h-4" /> View Site</a>
                    <button onClick={logout} data-testid="admin-logout" className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/10"><LogOut className="w-4 h-4" /> Logout</button>
                </div>
            </aside>

            <div className="md:hidden fixed top-0 inset-x-0 z-30 glass p-3 flex gap-2 overflow-x-auto">
                {nav.map((n) => <button key={n.key} onClick={() => setTab(n.key)} className={`px-3 py-1.5 rounded-lg text-xs whitespace-nowrap ${tab === n.key ? "bg-secondary text-primary" : "text-muted-foreground"}`}>{n.label}</button>)}
            </div>

            <main className="flex-1 p-5 md:p-10 pt-20 md:pt-10 max-w-4xl overflow-x-hidden">
                {tab === "config" ? <ConfigEditor token={token} /> : tab === "contacts" ? <Contacts token={token} /> : <Editor token={token} type={tab} />}
            </main>
        </div>
    );
}
